import { Headshot } from './Headshot';
import { LegTags } from './OnCourtTag';
import { useState } from 'react';
import { Archive, RotateCcw, Trash2 } from 'lucide-react';
import InjuryDetails from './InjuryDetails';
import { fmtPT, ptText } from '../lib/ptTime';
import ShootingLine from './ShootingLine';
import TicketMoney from './TicketMoney';
import { ticketLabel } from './SharedPicks';
import DeadStamp from './DeadStamp';
import PlatformBadge from './PlatformBadge';
import Possession, { LiveLine, RedZoneBar, ticketInRedZone } from './Possession';
import GameTracker, { singleGame } from './LiveField';
import TeamLogo, { LegTeam, multiTeam } from './TeamLogo';
import { ticketDate, isVoid, uploadedAt, missingLegs, offDayLegs, legKey, sortedLegs, blinkClass } from './ticketDate';
import SettleButtons from './SettleButtons';
import { BackInTag } from './BackInTag';
import { FoulTroubleBar } from './FoulTag';
import { GameInjuryBar } from './GameInjuryBar';
import { LiveClock } from './LiveClock';

const CELL = {
  neutral: 'bg-zinc-900 border-zinc-800 text-zinc-300',
  yellow: 'bg-yellow-400/5 border-yellow-400 text-yellow-200',
  orange: 'bg-orange-500/20 border-orange-500 text-orange-100',
  blue: 'bg-sky-500/20 border-sky-400 text-sky-100',
  purple: 'bg-purple-500/25 border-purple-400 text-purple-100',
  green: 'bg-emerald-500/20 border-emerald-400 text-emerald-100',
  red: 'bg-red-500/25 border-red-500 text-red-100',
};

export const colorOf = (leg) => (leg.performance_status === 'LIVE_TIED' ? 'yellow' : leg.color_token || 'neutral');
const usd = (n) => (n ? `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—');
export const short = (name) => String(name || '').replace(/\s*(moneyline|ml)$/i, '').trim();
const STAT = {
  passing_yards: 'pass yds', rushing_yards: 'rush yds', rushing_attempts: 'rush att', passing_attempts: 'pass att', passing_completions: 'comp', receiving_yards: 'rec yds', receptions: 'rec',
  passing_touchdowns: 'pass TD', rushing_touchdowns: 'rush TD', receiving_touchdowns: 'rec TD',
  touchdowns: 'TD', anytime_touchdown: 'TD', anytime_td: 'TD', points: 'pts', rebounds: 'reb', assists: 'ast', threes: '3PM', steals: 'stl', blocks: 'blk', stocks: 'stl+blk', dd: 'DD',
};
const statLabel = (k) => STAT[k] || String(k || '').replace(/_/g, ' ');
const bt = (leg) => (leg.bet_type || '').toLowerCase();
const isProp = (leg) => bt(leg) === 'player_prop' || !!leg.player_name;
const started = (leg) => leg.performance_status !== 'PREGAME' && leg.live_score_home != null;
const firstNum = (s, signed) => { const m = String(s || '').match(signed ? /[+-]\d+(\.\d+)?/ : /\d+(\.\d+)?/); return m ? m[0] : null; };

// Picked team's score first: "21–7".
const scoreText = (leg) => {
  if (!started(leg)) return '';
  const sel = short(leg.selection).toLowerCase();
  const home = String(leg.home_team || '').toLowerCase();
  const pickedHome = leg.picked_side ? leg.picked_side === 'home' : (home && (home.includes(sel.split(' ')[0]) || sel.includes(home.split(' ')[0])));
  const [mine, theirs] = pickedHome ? [leg.live_score_home, leg.live_score_away] : [leg.live_score_away, leg.live_score_home];
  return bt(leg) === 'total' ? `${mine + theirs} pts` : `${mine}–${theirs}`;
};

// "DeMar DeRozan" → "D. DeRozan"; long last names cut so the jersey number always fits.
export const shortName = (full) => {
  const parts = String(full).trim().split(/\s+/).filter((p) => !/^(jr|sr|ii|iii|iv)\.?$/i.test(p));
  if (parts.length < 2) return full;
  const last = parts.slice(1).join(' ');
  return `${parts[0][0]}. ${last.length > 10 ? `${last.slice(0, 8)}…` : last}`;
};

// Name line: "Mahomes", "Texas [W]", "Oregon -7.5", "OVER 50.5".
const NameLine = ({ leg }) => {
  if (isProp(leg)) return (
    <>
      {leg.league === 'nba' && <Headshot leg={leg} size={32} badge={false} testId={`board-leg-headshot-${leg.id}`} />}
      <TeamLogo leg={leg} />
      <span className="truncate font-semibold text-[11px] min-w-0" data-testid={`board-leg-name-${leg.id}`}>{leg.player_name ? shortName(leg.player_name) : short(leg.selection)}</span>
      {leg.jersey && <span className="shrink-0 font-semibold text-[11px]" data-testid={`board-leg-jersey-${leg.id}`}>#{leg.jersey}</span>}
    </>
  );
  if (bt(leg) === 'moneyline') {
    return (
      <>
        <TeamLogo leg={leg} />
        <span className="truncate font-semibold text-[11px] min-w-0">{short(leg.selection)}</span>
        <span className="shrink-0 rounded bg-white/15 px-1 text-[9px] font-black leading-4" data-testid={`board-leg-w-${leg.id}`}>W</span>
      </>
    );
  }
  return <><TeamLogo leg={leg} /><span className="truncate font-semibold text-[11px] min-w-0">{short(leg.selection)}</span></>;
};

// Main stat line: props "187/275.5 pass yds", teams the live score.
export const mainText = (leg) => {
  if (isProp(leg)) {
    const target = leg.required_target ?? leg.line ?? firstNum(leg.selection);
    const label = leg.stat_target ? statLabel(leg.stat_target)
      : (String(leg.selection || '').match(/\d(?:\.\d+)?\s+(.+)$/)?.[1] || '').replace(/yards?/i, 'yds');
    return `${leg.current_value ?? '–'}/${target ?? '?'} ${label}`.trim();
  }
  return scoreText(leg);
};

const AVAIL = {
  out: 'bg-red-600 text-white',
  questionable: 'bg-orange-500 text-black',
  possibly_out: 'bg-orange-500/80 text-black',
};

const isUnverified = (leg) => leg.match_confidence === 'low' || (leg.match_status === 'unmatched' && !['won', 'lost', 'push'].includes(leg.status))
  || (isProp(leg) && started(leg) && leg.current_value == null && !String(leg.performance_status || '').startsWith('FINAL'));

export const countLegs = (legs) => {
  const c = { won: 0, metLive: 0, pending: 0, unverified: 0, lost: 0 };
  legs.forEach((l) => {
    const ps = l.performance_status || '';
    if (ps === 'FINAL_MISS' || ps === 'FINAL_LOSS' || l.status === 'lost') c.lost += 1;
    else if (ps === 'FINAL_HIT' || ps === 'FINAL_WIN' || l.status === 'won') c.won += 1;
    else if (ps === 'LIVE_REACHED' || ps === 'RECORDED_HIT') c.metLive += 1;
    else if (isUnverified(l)) c.unverified += 1;
    else c.pending += 1;
  });
  return c;
};

const needUnit = (leg, n) => {
  const st = String(leg.stat_target || leg.selection || '').toLowerCase();
  if (/td|touchdown/.test(st)) return n === 1 ? 'TD' : 'TDs';
  if (/yard/.test(st)) return n === 1 ? 'yd' : 'yds';
  if (/attempt|carr/.test(st)) return n === 1 ? 'carry' : 'carries';
  return '';
};

// Plain-language status: "WON ✓", "MET LIVE ✓", "needs 1 more yd", "Stats unavailable".
const goalText = (leg) => {
  const ps = leg.performance_status || '';
  if (ps === 'FINAL_HIT' || ps === 'FINAL_WIN' || leg.status === 'won') return 'WON ✓';
  if (ps === 'LIVE_REACHED' || ps === 'RECORDED_HIT') return 'MET LIVE ✓';
  if (ps === 'FINAL_MISS' || ps === 'FINAL_LOSS' || leg.status === 'lost') return 'LOST';
  if (isVoid(leg)) return leg.status === 'push' ? 'PUSH' : 'VOID — did not play';
  if (leg.slip_check) return 'Check this — not in slip game';
  if (leg.match_confidence === 'low') return 'Unverified — check game';
  if (!started(leg) && leg.current_value == null) return '';
  if (isProp(leg)) {
    if (leg.current_value == null) return 'Stats unavailable';
    if (leg.remaining_needed > 0 && ['blue', 'purple'].includes(leg.color_token)) return `${leg.color_token === 'purple' ? '50%' : '75%'} · needs ${leg.remaining_needed} more ${needUnit(leg, leg.remaining_needed)}`.trim();
    if (leg.remaining_needed > 0) return `needs ${leg.remaining_needed} more ${needUnit(leg, leg.remaining_needed)}`.trim();
    return ps === 'LIVE_LEADING' ? 'under — winning' : '';
  }
  if (leg.cover_margin != null) return `${leg.cover_margin > 0 ? 'covering' : 'short'} ${leg.cover_margin > 0 ? '+' : ''}${leg.cover_margin}`;
  if (bt(leg) === 'moneyline') return ps === 'LIVE_LEADING' ? 'leading' : ps === 'LIVE_TIED' ? 'tied' : ps === 'LIVE_TRAILING' ? 'trailing' : '';
  return '';
};

const statusText = (leg) => {
  const ps = leg.performance_status || '';
  if (ps.startsWith('FINAL')) return 'F';
  if (ps === 'PREGAME') return leg.event_date ? fmtPT(leg.event_date) : ptText(leg.game_time || leg.live_period || '').replace(/^\d+\/\d+\s*-\s*/, '');
  return (leg.live_period || '').replace(/Quarter/i, 'Q').replace(/Half/i, 'H');
};

const LegCell = ({ leg, offDay, multi, ticketId }) => {
  const c = colorOf(leg);
  const [inj, setInj] = useState(false);
  return (
    <div className={`relative rounded border-2 px-1.5 py-0.5 leading-tight ${CELL[c] || CELL.neutral}${blinkClass(leg.blink, c)}`}
      data-testid={`board-leg-${leg.id}`} data-blink={leg.blink ? 'true' : undefined} data-color={c}
      title={`${leg.selection || ''}${leg.home_team ? ` · ${leg.away_team} @ ${leg.home_team}` : ''} · ${leg.stats_source || 'ESPN'} ${leg.last_check_at ? new Date(leg.last_check_at).toLocaleTimeString() : ''}${leg.stale ? ' (STALE)' : ''}`}>
      <LiveLine leg={leg} testid={`board-leg-live-${leg.id}`} />
      <div className="flex items-center gap-1">
        <NameLine leg={leg} />
        {multi && (leg.bet_type || '').toLowerCase() !== 'player_prop' && <LegTeam leg={leg} size={15} />}
      </div>
      <div className="font-mono text-[11px] font-bold truncate" data-testid={`board-leg-stat-${leg.id}`}>{mainText(leg) || '\u00a0'}</div>
      <div className="flex items-center justify-between gap-1">
        <span className="text-[9px] font-semibold uppercase truncate" data-testid={`board-leg-goal-${leg.id}`}>{goalText(leg)}</span>
        <span className="text-[9px] opacity-75 truncate">{offDay && /^(TBD)?$/i.test(statusText(leg).trim()) ? offDay : leg.clock_running ? <LiveClock leg={leg} testid={`board-leg-clock-${leg.id}`} /> : statusText(leg)}</span>
      </div>
      <ShootingLine leg={leg} className="text-[10px] font-semibold opacity-90 truncate" />
      <LegTags leg={leg} />
      {leg.slip_check && <div className="mt-0.5 rounded bg-amber-500/25 px-1 text-[9px] font-bold leading-4 text-amber-200 truncate" title={leg.slip_check} data-testid={`board-leg-slipcheck-${leg.id}`}>⚠ Check: {leg.slip_matchup}</div>}
      {offDay && <div className="mt-0.5 rounded bg-amber-500/25 px-1 text-[9px] font-bold leading-4 text-amber-200" data-testid={`board-leg-offday-${leg.id}`}>Plays {offDay} — different day</div>}
      <Possession leg={leg} compact />
      <RedZoneBar leg={leg} />
      <BackInTag leg={leg} />
      <GameInjuryBar leg={leg} onClick={() => setInj(true)} />
      <FoulTroubleBar leg={leg} />
      {leg.bench_note && !leg.availability && <div className="mt-0.5 text-[9px] opacity-80 truncate" data-testid={`leg-bench-${leg.id}`}>{leg.bench_note}</div>}
      {leg.availability && !(leg.game_injury?.stage && !leg.game_injury?.final) && (
        <button type="button" onClick={(e) => { e.stopPropagation(); setInj(true); }}
          className={`mt-0.5 w-full text-left rounded px-1 text-[9px] font-bold leading-4 truncate underline decoration-dotted relative z-20 ${AVAIL[leg.availability.level] || AVAIL.questionable}`}
          title="Tap for injury details" data-testid={`board-leg-injury-${leg.id}`}>
          ⚠ {leg.availability.text}
        </button>
      )}
      {inj && <InjuryDetails ticketId={ticketId} leg={leg} open={inj} onClose={() => setInj(false)} />}
    </div>
  );
};

const TicketColumn = ({ ticket, onDelete, onArchive, onSettle, onChanged, archived }) => {
  const legs = ticket.legs || [];
  const c = countLegs(legs);
  const won = legs.filter((l) => colorOf(l) === 'green').length;
  const man = ticket.manual_result;
  const voided = legs.filter(isVoid).length;
  const lost = man ? (man === 'lost' ? 1 : 0) : c.lost || (ticket.status === 'lost' ? 1 : 0);
  const allHit = man ? man === 'won' : !missingLegs(ticket) && (ticket.status === 'won' || (legs.length > 0 && !lost && won > 0 && won + voided === legs.length));
  const stale = legs.some((l) => l.stale);
  const updated = legs.map((l) => l.last_check_at || l.last_refreshed_at).filter(Boolean).sort().pop();
  const countMismatch = ticket.leg_count && Number(ticket.leg_count) !== legs.length;
  const injured = legs.filter((l) => l.availability).length;
  const off = offDayLegs(ticket);
  const offN = Object.keys(off).length;
  const game = singleGame(ticket);
  return (
    <div className={`relative rounded-lg p-1.5 space-y-1 min-w-0 ${ticketInRedZone(ticket) ? 'border-2 border-red-500 shadow-[0_0_10px_rgba(239,68,68,0.6)]' : 'border'} ${lost ? 'border-red-500/50 bg-red-950/20' : allHit ? 'border-emerald-400/70 bg-emerald-950/20' : ticketInRedZone(ticket) ? 'bg-zinc-900/60' : 'border-zinc-700 bg-zinc-900/60'}`}
      data-testid={`board-ticket-${ticket.id}`} data-redzone={ticketInRedZone(ticket) ? 'true' : undefined}>
      {lost > 0 && <DeadStamp ticketId={ticket.id} />}
      {allHit && <DeadStamp ticketId={ticket.id} variant="won" />}
      <PlatformBadge ticket={ticket} onChanged={onChanged} className="!w-5 !h-5 !text-[10px]" />
      <p className="text-right pr-7 pt-1 text-[9px] font-semibold leading-none text-zinc-300" data-testid={`board-date-${ticket.id}`}>{ticketDate(ticket)}</p>
      <p className="text-right pr-7 mt-0.5 text-[8px] leading-none text-zinc-500" data-testid={`board-uploaded-${ticket.id}`}>{uploadedAt(ticket)}</p>
      <div className="flex items-start justify-between gap-1">
        <div className="min-w-0">
          <p className={`text-[11px] font-bold text-white truncate`} title={ticketLabel(ticket)}>{ticketLabel(ticket)}</p>
          <TicketMoney ticket={ticket} onChanged={onChanged} compact />
          <p className={`text-[10px] font-semibold ${lost ? 'text-red-400' : allHit ? 'text-emerald-300' : 'text-sky-300'}`}>
            {lost ? 'Lost' : allHit ? 'Won' : ticket.status === 'needs_settlement' ? 'Needs sportsbook settlement' : 'Alive'} · {won}/{legs.length} hit
          </p>
          <p className="text-[9px] text-zinc-400 leading-tight" data-testid={`board-counts-${ticket.id}`}>
            {c.won} won · {c.metLive} met live · {c.pending} pending · {c.unverified} unverified · {c.lost} lost{voided ? ` · ${voided} void` : ''}
          </p>
          <p className="text-[9px] text-zinc-500 leading-tight" data-testid={`board-updated-${ticket.id}`}>
            ESPN · {updated ? new Date(updated).toLocaleTimeString() : 'not checked yet'}
            {stale && <span className="ml-1 rounded bg-red-500/20 px-1 font-bold text-red-300" data-testid={`board-stale-${ticket.id}`}>STALE</span>}
          </p>
          {injured > 0 && (
            <p className="text-[9px] font-bold text-orange-300" data-testid={`board-injury-alert-${ticket.id}`}>
              ⚠ {injured} player{injured > 1 ? 's' : ''} with injury news
            </p>
          )}
          {offN > 0 && (
            <p className="text-[9px] font-semibold text-amber-300" data-testid={`board-offday-warn-${ticket.id}`}>
              ⚠ {offN} game{offN > 1 ? 's' : ''} on a different day than the rest
            </p>
          )}
          {countMismatch && (
            <p className="text-[9px] font-semibold text-amber-300" data-testid={`board-legcount-warn-${ticket.id}`}>
              Ticket says {ticket.leg_count} legs — {legs.length} read. Check upload.
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5 relative z-20 pr-6">
          {archived ? (
            <button type="button" onClick={() => onArchive(ticket, false)} className="text-zinc-400 hover:text-amber-300" title="Restore to board"
              data-testid={`board-restore-${ticket.id}`}>
              <RotateCcw className="w-3 h-3" />
            </button>
          ) : (lost > 0 || allHit) && onArchive && (
            <button type="button" onClick={() => onArchive(ticket, true)} className="text-emerald-400 hover:text-emerald-300" title="Save to archive"
              data-testid={`board-archive-${ticket.id}`}>
              <Archive className="w-3 h-3" />
            </button>
          )}
          <button type="button" onClick={() => onDelete(ticket)} className="text-zinc-500 hover:text-red-400" title="Delete ticket"
            data-testid={`board-delete-${ticket.id}`}>
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
      {onSettle && <SettleButtons ticket={ticket} onSettle={onSettle} small />}
      {game && !lost && !allHit && <GameTracker league={game.league} eventId={game.eventId} leg={game.leg} />}
      {sortedLegs(legs).map(([leg, i]) => <LegCell key={legKey(leg, i)} leg={leg} ticketId={ticket.id} offDay={off[legKey(leg, i)]} multi={multiTeam(ticket)} />)}
    </div>
  );
};

const COLS = {
  single: 'grid-cols-1',
  two: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6',
  three: 'grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8',
};

// layout: 'two' = 2 tickets per row on a phone; 'three' = the same cards shrunk to fit 3 per row.
export default function TicketBoard({ tickets, onDelete, onArchive, onSettle, onChanged, archived = false, layout = 'two' }) {
  const small = layout === 'three';
  return (
    <div className={`grid ${COLS[layout]} ${small ? 'gap-1' : 'gap-1.5'} items-start`} data-testid={small ? 'ticket-board-3' : 'ticket-board'}>
      {tickets.map((t) => (
        <div key={t.id} className="min-w-0" style={small ? { zoom: 0.72 } : undefined}>
          <TicketColumn ticket={t} onDelete={onDelete} onArchive={onArchive} onSettle={onSettle} onChanged={onChanged} archived={archived} />
        </div>
      ))}
    </div>
  );
}
